import { CityTaxExonerationCode, CreditType, CustomerInput, GenderType, MailContactType, PhoneContactType, Range, WebContactType } from 'src/api/api.generated'
import { Language, languageList } from 'src/api/language'

export type MailContactFormData = {
  isDefault: boolean
  order: number
  type: MailContactType
  value: string
}

export type PhoneContactFormData = {
  isDefault: boolean
  order: number
  type: PhoneContactType
  value: string
}

export type WebContactFormData = {
  isDefault: boolean
  order: number
  type: WebContactType
  value: string
}

export type PrivacyFormData = {
  dataThirdPartyConsent: boolean
  dataThirdPartyConsentDate?: Date
  marketingConsent: boolean
  marketingConsentDate?: Date
  managementConsent: boolean
  managementConsentDate?: Date
  profiledMarketingConsent: boolean
  profiledMarketingConsentDate?: Date
}

export type AddressFormData = {
  countryId: string | null
  address: string | null
  city: string | null
  municipalityId: string | null
  postalCode: string | null
  province: string | null
}

export type PersonIDFormData = {
  documentId: string
  number: string
  releaseBy?: string
  expirationDate: Date
  countryId: string
  city?: string
}

export type PersonFormData = {
  type: 'PERSON'
  lastName: string
  firstName: string
  name: string
  mailContacts: MailContactFormData[]
  phoneContacts: PhoneContactFormData[]
  privacy: PrivacyFormData
  language: Language
  gender?: GenderType
  address: AddressFormData | null
  birthDate?: Date
  fiscalCode?: string
  birthAddress: AddressFormData | null
  nationalityId: string | null
  identificationDocument?: PersonIDFormData
  relatedCompanyId?: string | null
  tagsId?: string[]
  cityTax?: {
    contracts?: Range[] | null
    exoneration?: CityTaxExonerationCode | null
  } | null
}

export function getLookupPersonFormData(lastName: string, firstName: string, language: Language): PersonFormData {
  return {
    type: 'PERSON',
    lastName: lastName,
    firstName: firstName,
    name: `${lastName} ${firstName}`,
    mailContacts: [],
    phoneContacts: [],
    privacy: {
      dataThirdPartyConsent: false,
      marketingConsent: false,
      managementConsent: false,
      profiledMarketingConsent: false,
    },
    language: language,
    address: null,
    birthAddress: null,
    nationalityId: null,
  }
}

export type CompanyFormData = {
  type: 'COMPANY'
  privacy: PrivacyFormData
  language: Language
  physicalPerson: boolean
  name: string
  firstName?: string
  lastName?: string
  mailContacts: MailContactFormData[]
  phoneContacts: PhoneContactFormData[]
  webContacts: WebContactFormData[]
  address: AddressFormData | null
  vatNumber?: string
  fiscalCode?: string
  sdiCode?: string
  tagsId?: string[]
  credit: CreditType
  splitPayment: boolean
  riferimentoAmministrazione: boolean
  stayVatId: string | null
}

export function getLookupCompanyFormData(name: string, language: Language): CompanyFormData {
  return {
    type: 'COMPANY',
    privacy: {
      dataThirdPartyConsent: false,
      marketingConsent: false,
      managementConsent: false,
      profiledMarketingConsent: false,
    },
    language: language,
    physicalPerson: false,
    name: name,
    lastName: undefined,
    firstName: undefined,
    mailContacts: [],
    phoneContacts: [],
    webContacts: [],
    address: null,
    vatNumber: undefined,
    fiscalCode: undefined,
    sdiCode: undefined,
    tagsId: [],
    credit: CreditType.NC,
    splitPayment: false,
    riferimentoAmministrazione: false,
    stayVatId: null,
  }
}

export type CustomerFormData = PersonFormData | CompanyFormData

export const autoCompleteLanguages = Language.map((l) => {
  const info = languageList[l]
  return { id: l, label: `${info.name} (${info.nativeName})` }
})

export function getCustomerInput(form: CustomerFormData, tenantId: string): CustomerInput {
  if (form.type === 'PERSON') {
    const input: CustomerInput = {
      tenantId: tenantId,
      type: form.type,
      lastName: form.lastName,
      firstName: form.firstName,
      name: form.name,
      mailContacts: form.mailContacts,
      phoneContacts: form.phoneContacts,
      privacy: form.privacy,
      language: form.language,
      person: {
        gender: form.gender,
        birthDate: form.birthDate,
        birthAddress: form.birthAddress,
        identificationDocument: form.identificationDocument,
        nationalityId: form.nationalityId,
      },
      address: form.address,
      fiscalCode: form.fiscalCode,
      tagsId: form.tagsId,
      cityTax: form.cityTax,
      relatedCompanyId: form.relatedCompanyId,
    }
    return input
  } else {
    const input: CustomerInput = {
      tenantId: tenantId,
      type: form.type,
      lastName: form.lastName,
      firstName: form.firstName,
      name: form.name,
      mailContacts: form.mailContacts,
      phoneContacts: form.phoneContacts,
      webContacts: form.webContacts,
      privacy: form.privacy,
      language: form.language,
      company: {
        physicalPerson: form.physicalPerson,
        splitPayment: form.splitPayment,
        sdiCode: form.sdiCode,
        defaultCredit: form.credit,
        felOptions: {
          riferimentoAmministrazione: form.riferimentoAmministrazione,
        },
      },
      address: form.address,
      vatNumber: form.vatNumber,
      fiscalCode: form.fiscalCode,
      tagsId: form.tagsId,
      stayVatId: form.stayVatId,
    }
    return input
  }
}

export type FocusedField = 'FISCAL_CODE' | 'VAT_NUMBER' | 'ADDRESS' | 'EMAIL'
